import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Chat = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-4');
  const messagesEndRef = useRef(null);

  const models = [
    { id: 'gpt-4', name: 'GPT-4', credits: 15, description: 'Más potente y preciso' },
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', credits: 5, description: 'Rápido y eficiente' },
    { id: 'claude-3-5-sonnet', name: 'Claude 3.5', credits: 12, description: 'Excelente razonamiento' },
    { id: 'gemini-pro', name: 'Gemini Pro', credits: 10, description: 'Versátil y creativo' },
  ];

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const createNewConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/conversations`,
        { title: 'Nueva conversación' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newConv = response.data.conversation;
      setConversations([newConv, ...conversations]);
      setCurrentConversation(newConv);
      setMessages([]);
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Error al crear conversación');
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/chat/conversations/${conversationId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentConversation(response.data.conversation);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    // Si no hay conversación activa, crear una nueva
    if (!currentConversation) {
      await createNewConversation();
      // Esperar un momento para que se cree la conversación
      setTimeout(() => sendMessageToConversation(), 500);
      return;
    }

    await sendMessageToConversation();
  };

  const sendMessageToConversation = async () => {
    if (!currentConversation) return;

    const messageText = inputMessage;
    setInputMessage('');
    setLoading(true);

    // Añadir mensaje del usuario inmediatamente
    const userMsg = {
      role: 'user',
      content: messageText,
      created_at: new Date().toISOString()
    };
    setMessages([...messages, userMsg]);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/conversations/${currentConversation.id}/messages`,
        {
          message: messageText,
          model: selectedModel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Añadir respuesta del asistente
      setMessages(prev => [...prev, response.data.assistant_message]);
      loadConversations(); // Actualizar lista de conversaciones
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = error.response?.data?.detail || 'Error al enviar mensaje';
      alert(errorMsg);
      // Remover el mensaje del usuario si hubo error
      setMessages(messages);
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta conversación?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/chat/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(conversations.filter(c => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Error al eliminar conversación');
    }
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen flex flex-col">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar con conversaciones */}
        <div className="w-80 bg-white dark:bg-[#1a2530] border-r border-gray-200 dark:border-[#283039] overflow-y-auto">
          <div className="p-4">
            <button
              onClick={createNewConversation}
              className="w-full py-3 px-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
            >
              <span className="text-xl">➕</span>
              Nueva Conversación
            </button>

            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                Conversaciones
              </h3>
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      currentConversation?.id === conv.id
                        ? 'bg-primary/10 border border-primary'
                        : 'hover:bg-gray-50 dark:hover:bg-[#283039]'
                    }`}
                    onClick={() => loadConversation(conv.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {conv.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {conv.message_count || 0} mensajes
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conv.id);
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <span className="text-sm">🗑️</span>
                      </button>
                    </div>
                  </div>
                ))}
                {conversations.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No hay conversaciones aún
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Área de chat */}
        <div className="flex-1 flex flex-col bg-gray-50 dark:bg-background-dark">
          {/* Header del chat */}
          <div className="bg-white dark:bg-[#1a2530] border-b border-gray-200 dark:border-[#283039] p-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {currentConversation?.title || 'Chat IA'}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.name} · Créditos: {user?.credits?.toLocaleString() || 0}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-[#3b4754] rounded-lg focus:ring-2 focus:ring-primary bg-white dark:bg-[#283039] text-gray-900 dark:text-white"
                >
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.credits} créditos)
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {!currentConversation && messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <span className="text-6xl text-gray-300 dark:text-gray-600 mb-4">
                  💬
                </span>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Inicia una nueva conversación
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Selecciona un modelo y comienza a chatear con IA
                </p>
              </div>
            )}

            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3xl rounded-lg p-4 ${
                    msg.role === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-white dark:bg-[#1a2530] text-gray-900 dark:text-white border border-gray-200 dark:border-[#283039]'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-[#1a2530] rounded-lg p-4 border border-gray-200 dark:border-[#283039]">
                  <div className="flex items-center gap-2">
                    <div className="animate-pulse flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Generando respuesta...
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input de mensaje */}
          <div className="bg-white dark:bg-[#1a2530] border-t border-gray-200 dark:border-[#283039] p-4">
            <div className="flex items-end gap-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Escribe tu mensaje..."
                rows="3"
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-[#3b4754] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-[#283039] text-gray-900 dark:text-white resize-none"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !inputMessage.trim()}
                className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <span className="text-xl">📤</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Presiona Enter para enviar, Shift+Enter para nueva línea
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Footer />
      </div>
    </div>
  );
};

export default Chat;
